// A workspace's outstanding invitations: offering a seat to an address that is **not** in the
// organization yet, reading what is still pending, and withdrawing one. The key is
// `assets.members.invite`, held in the workspace; the create asks for the organization's
// `auth.members.invite` too, because what it grants is a membership and not only a seat.
//
// **An invitation is an address, never a link.** There is no join URL, no share token and nothing
// to copy: `base-auth` mints the secret and the message is the only place it travels, so a list an
// administrator can read here is never a list anybody may accept from.
//
// **An address that already belongs to the organization is seated, not invited** — that is
// `addWorkspaceMember` in `./workspaces`, and this route answers `409` naming it.

import { api } from '../client'
import type { components } from '../schema'

export type WorkspaceInvitationView = components['schemas']['WorkspaceInvitationView']
export type CreateWorkspaceInvitationRequest = components['schemas']['CreateWorkspaceInvitationRequest']

function invitationsPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/invitations`
}

/**
 * What this workspace is still waiting on: every live, unconsumed, unexpired offer of a seat in it,
 * newest first. **Whole, with no cursor** — an organization mints a bounded number of invitations a
 * day and each lapses within the week, so the set is bounded by that product. An invitation that
 * was accepted, withdrawn or has lapsed is not here. 403 without `assets.members.invite`.
 */
export function listWorkspaceInvitations(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<WorkspaceInvitationView[]> {
  return api.get<WorkspaceInvitationView[]>(invitationsPath(workspaceId), undefined, signal)
}

/**
 * Offer one address a seat here, under the workspace `role` the body names: it mints the
 * organization invitation — organization role `member`, never the caller's choice — with the
 * workspace seat attached, and sends the message.
 *
 * **It is also the re-invite.** Posting the same address again supersedes its previous offer and
 * carries that offer's seats in other workspaces onto the new invitation, so a second shelf never
 * silently withdraws the first; every other workspace's pending list then names a different
 * invitation uid.
 *
 * **With `supersedes` and no `role` it is a re-send** of that pending invitation: the seat under the
 * role it offers, the organization role the invitation grants — another than `member` for an
 * organization invitation with a seat here, whose message then states it —, and credited to
 * whoever sent it while they are still a member of the organization, to the caller once they have
 * left.
 *
 * 400 a malformed address, a role neither vocabulary carries — on a re-send, its seat's, deleted
 * since —, a re-send naming a `role`, or a new offer naming none; 403 the caller lacks
 * `assets.members.invite` here, or `auth.members.invite` in the organization, or may not grant
 * `member` — on a re-send, the role the invitation grants; 404 no such workspace in this
 * organization, or `supersedes` names no pending invitation of this address with a seat here —
 * accepted, withdrawn, superseded or lapsed since the list was drawn; 409 the address already
 * belongs to a live member — seat them with `addWorkspaceMember` instead; 429 the re-invite cooldown
 * or the organization's daily invitation budget, which revoking never refunds; 503 no mail relay, so
 * the secret has nowhere to travel and nothing is minted.
 */
export function createWorkspaceInvitation(
  workspaceId: string,
  body: CreateWorkspaceInvitationRequest,
): Promise<WorkspaceInvitationView> {
  return api.post<WorkspaceInvitationView>(invitationsPath(workspaceId), body)
}

/**
 * Withdraw one outstanding invitation: its emailed link stops working and the seat it promised
 * stops describing anything.
 *
 * **The whole invitation goes, not this workspace's seat in it** — one address holds one offer, so
 * an invitee named on two workspaces leaves both lists. Offer it again with
 * `createWorkspaceInvitation`.
 *
 * 403 without `assets.members.invite` here; one uniform 404 over six other fates — no such
 * invitation, one of another organization, one carrying no seat in this workspace, one already
 * accepted, one already withdrawn, and one that has lapsed.
 */
export function cancelWorkspaceInvitation(workspaceId: string, invitationId: string): Promise<undefined> {
  return api.del<undefined>(`${invitationsPath(workspaceId)}/${encodeURIComponent(invitationId)}`)
}
